import useGetApi from '../useGetApi';
import config from 'src/config';
import type {
  CalculatedBreakdownExportResponse,
  CalculatedBreakdownExportData,
  InvoiceLineItemExportResponse,
  InvoiceLineItemExportData,
  ProductGroupExportResponse,
  ProductGroupExportData,
  SupplierPaymentExportResponse,
  SupplierPaymentExportData,
  PaymentAllocationExportResponse,
  PaymentAllocationExportData,
  SalesMarginExportResponse,
  SalesMarginExportData,
} from '@erp/shared-types';

export function useCalculatedBreakdownsExport(
  filters: Record<string, unknown>,
  enabled = true,
) {
  return useGetApi<
    CalculatedBreakdownExportResponse,
    CalculatedBreakdownExportData
  >({
    endpoint: 'rebates/exports/calculated-breakdowns',
    queryParameters: filters,
    options: {
      enabled,
    },
  });
}

export function useInvoiceLineItemsExport(
  filters: Record<string, unknown>,
  enabled = true,
) {
  return useGetApi<InvoiceLineItemExportResponse, InvoiceLineItemExportData>({
    endpoint: 'rebates/exports/invoice-line-items',
    queryParameters: filters,
    options: {
      enabled,
    },
  });
}

export function useProductGroupsExport(
  filters: Record<string, unknown>,
  enabled = true,
) {
  return useGetApi<ProductGroupExportResponse, ProductGroupExportData>({
    endpoint: 'rebates/exports/product-groups',
    queryParameters: filters,
    options: {
      enabled,
    },
  });
}

export function useSupplierPaymentsExport(
  filters: Record<string, unknown>,
  enabled = true,
) {
  return useGetApi<SupplierPaymentExportResponse, SupplierPaymentExportData>({
    endpoint: 'rebates/exports/supplier-payments',
    queryParameters: filters,
    options: {
      enabled,
    },
  });
}

export function usePaymentAllocationsExport(
  filters: Record<string, unknown>,
  enabled = true,
) {
  return useGetApi<
    PaymentAllocationExportResponse,
    PaymentAllocationExportData
  >({
    endpoint: 'rebates/exports/payment-allocations',
    queryParameters: filters,
    options: {
      enabled,
    },
  });
}

export function useSalesMarginsExport(
  filters: Record<string, unknown>,
  enabled = true,
) {
  return useGetApi<SalesMarginExportResponse, SalesMarginExportData>({
    endpoint: 'rebates/exports/sales-margins',
    queryParameters: filters,
    options: {
      enabled,
    },
  });
}

function buildCsvQueryString(filters: Record<string, unknown>): string {
  const params = new URLSearchParams();

  // Only include filter params, exclude pagination params
  if (filters.startDate) params.append('startDate', String(filters.startDate));
  if (filters.endDate) params.append('endDate', String(filters.endDate));
  if (filters.month !== undefined && filters.month !== null)
    params.append('month', String(filters.month));
  if (filters.viewMode) params.append('viewMode', String(filters.viewMode));
  if (filters.lineType) params.append('lineType', String(filters.lineType));
  if (filters.agreementIds)
    params.append('agreementIds', String(filters.agreementIds));
  if (filters.agreementTagIds)
    params.append('agreementTagIds', String(filters.agreementTagIds));
  if (filters.supplierIds)
    params.append('supplierIds', String(filters.supplierIds));
  if (filters.supplierTagIds)
    params.append('supplierTagIds', String(filters.supplierTagIds));
  if (filters.productGroupTagIds)
    params.append('productGroupTagIds', String(filters.productGroupTagIds));
  if (filters.divisionIds)
    params.append('divisionIds', String(filters.divisionIds));
  if (filters.productFamilyDescs)
    params.append('productFamilyDescs', String(filters.productFamilyDescs));
  if (filters.shipTypes) params.append('shipTypes', String(filters.shipTypes));
  if (filters.type) params.append('type', String(filters.type));

  return params.toString();
}

async function downloadCsv(
  endpoint: string,
  filename: string,
  filters: Record<string, unknown>,
): Promise<void> {
  const queryString = buildCsvQueryString(filters);
  const url = `${config.apiBaseUrl}/${endpoint}${queryString ? `?${queryString}` : ''}`;

  try {
    const response = await fetch(url, {
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to download CSV');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error downloading CSV:', error);
    throw error;
  }
}

/**
 * Download calculated breakdowns as CSV
 */
export async function downloadCalculatedBreakdownsCsv(
  filters: Record<string, unknown>,
): Promise<void> {
  return downloadCsv(
    'rebates/exports/calculated-breakdowns/csv',
    'calculated-breakdowns.csv',
    filters,
  );
}

/**
 * Download invoice line items as CSV
 */
export async function downloadInvoiceLineItemsCsv(
  filters: Record<string, unknown>,
): Promise<void> {
  return downloadCsv(
    'rebates/exports/invoice-line-items/csv',
    'vendor-transactions.csv',
    filters,
  );
}

/**
 * Download product groups as CSV
 */
export async function downloadProductGroupsCsv(
  filters: Record<string, unknown>,
): Promise<void> {
  return downloadCsv(
    'rebates/exports/product-groups/csv',
    'product-groups.csv',
    filters,
  );
}

/**
 * Download supplier payments as CSV
 */
export async function downloadSupplierPaymentsCsv(
  filters: Record<string, unknown>,
): Promise<void> {
  return downloadCsv(
    'rebates/exports/supplier-payments/csv',
    'supplier-payments.csv',
    filters,
  );
}

/**
 * Download payment allocations as CSV
 */
export async function downloadPaymentAllocationsCsv(
  filters: Record<string, unknown>,
): Promise<void> {
  return downloadCsv(
    'rebates/exports/payment-allocations/csv',
    'payment-allocations.csv',
    filters,
  );
}

/**
 * Download sales margins as CSV
 */
export async function downloadSalesMarginsCsv(
  filters: Record<string, unknown>,
): Promise<void> {
  return downloadCsv(
    'rebates/exports/sales-margins/csv',
    'sales-margins.csv',
    filters,
  );
}

/**
 * Download profitability report as CSV
 */
export async function downloadProfitabilityReportCsv(
  filters: Record<string, unknown>,
): Promise<void> {
  return downloadCsv(
    'rebates/reports/profitability/csv',
    'profitability-report.csv',
    filters,
  );
}

/**
 * Delete sales margins for a given year
 */
export async function deleteSalesMarginsForYear(
  year: number,
): Promise<{ success: boolean; deletedCount: number; message: string }> {
  const url = `${config.apiBaseUrl}/rebates/exports/sales-margins?year=${year}`;

  const response = await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to delete sales margins');
  }

  const result = await response.json();
  return {
    success: result.data.success,
    deletedCount: result.data.deletedCount,
    message: result.message,
  };
}
